import { Typography, Form, Input, Button, DatePicker, Select, message, Upload, Alert, Row, Col } from 'antd';
import { InboxOutlined, ThunderboltOutlined, CheckCircleFilled, ArrowLeftOutlined, RocketOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { opportunityApi } from '@/api/opportunityApi';
import { documentApi } from '@/api/documentApi';
import PageHeader from '@/components/ui/PageHeader';
import type { OpportunityCreate } from '@/types/opportunity';
import type { UploadFile } from 'antd';
import dayjs from 'dayjs';

const { Text } = Typography;
const { TextArea } = Input;
const { Dragger } = Upload;

// How the proposal gets submitted; mirrors the backend's free-text field but
// offers the two common options.
const SUBMISSION_METHODS = ['Portal', 'Email'];

// Small "AI" pill rendered next to fields the AI pre-filled.
const AiBadge = () => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 3,
      marginLeft: 8,
      padding: '0 7px',
      height: 18,
      borderRadius: 999,
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '0.04em',
      color: 'var(--color-primary)',
      background: 'var(--color-primary-light)',
    }}
  >
    <ThunderboltOutlined style={{ fontSize: 9 }} /> AI
  </span>
);

const OpportunityCreatePage = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [extracting, setExtracting] = useState(false);
  const [aiFields, setAiFields] = useState<Set<string>>(new Set());
  const [aiResult, setAiResult] = useState<{ count: number; note?: string } | null>(null);

  const firstFile = (): File | undefined =>
    fileList.find((f) => f.originFileObj)?.originFileObj as File | undefined;

  // Upload-first AI intake: read the RFO doc and pre-fill the form.
  const handleExtract = async () => {
    const file = firstFile();
    if (!file) {
      message.info('Add an RFO/RFP document first');
      return;
    }
    setExtracting(true);
    setAiResult(null);
    try {
      const res = await opportunityApi.extractIntake(file);
      if (!res.extracted) {
        setAiResult({ count: 0, note: res.note });
        message.info(res.note || 'Could not extract details — please enter them manually.');
        return;
      }
      const filled = new Set<string>();
      const values: Record<string, unknown> = {};
      const set = (key: string, val?: string) => {
        if (val) {
          values[key] = val;
          filled.add(key);
        }
      };
      set('title', res.title);
      set('clientName', res.agencyName);
      set('description', res.description);
      set('sourceType', res.sourcePortal);
      set('sourceUrl', res.sourceUrl);
      set('rfoNumber', res.rfoNumber);
      if (res.submissionMethod) {
        // Normalize to one of the select options when it matches.
        const m = String(res.submissionMethod).trim();
        const match = SUBMISSION_METHODS.find((s) => s.toLowerCase() === m.toLowerCase());
        set('submissionMethod', match || m);
      }
      if (res.dueDate) {
        const d = dayjs(res.dueDate);
        if (d.isValid()) {
          values.deadline = d;
          filled.add('deadline');
        }
      }
      form.setFieldsValue(values);
      setAiFields(filled);
      setAiResult({ count: filled.size, note: res.note });
      message.success(`AI pre-filled ${filled.size} field${filled.size !== 1 ? 's' : ''} — review below.`);
    } catch (error) {
      console.error('Intake extraction failed:', error);
      message.error('AI extraction failed. Please enter the details manually.');
      setAiResult({ count: 0, note: 'Extraction failed.' });
    } finally {
      setExtracting(false);
    }
  };

  const onFinish = async (values: Record<string, unknown>) => {
    setLoading(true);
    try {
      const payload: OpportunityCreate = {
        title: values.title as string,
        clientName: values.clientName as string,
        description: values.description as string,
        rfoNumber: (values.rfoNumber as string) || undefined,
        submissionMethod: (values.submissionMethod as string) || undefined,
        sourceType: (values.sourceType as string) || '',
        sourceUrl: values.sourceUrl as string,
        deadline: values.deadline ? dayjs(values.deadline as string).format('YYYY-MM-DD') : undefined,
      };

      const created = await opportunityApi.create(payload);

      // Upload RFO documents if any were selected
      const filesToUpload = fileList.filter((f) => f.originFileObj);
      if (filesToUpload.length > 0) {
        let uploaded = 0;
        for (const f of filesToUpload) {
          try {
            await documentApi.uploadFile(
              f.originFileObj as File,
              created.id,
              'RFO_SOURCE',
              'PHASE_A',
            );
            uploaded++;
          } catch (uploadErr) {
            console.error(`Failed to upload ${f.name}:`, uploadErr);
          }
        }
        if (uploaded === filesToUpload.length) {
          message.success(`Opportunity created with ${uploaded} document${uploaded !== 1 ? 's' : ''}!`);
        } else {
          message.warning(`Opportunity created. ${uploaded}/${filesToUpload.length} documents uploaded.`);
        }
      } else {
        message.success('Opportunity created successfully!');
      }

      navigate(`/opportunities/${created.id}`);
    } catch (error) {
      console.error('Failed to create opportunity:', error);
      message.error('Failed to create opportunity. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const hasFile = !!firstFile();

  return (
    <div style={{ maxWidth: 840, margin: '0 auto' }}>
      <Button
        type="text"
        size="small"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/opportunities')}
        style={{ marginBottom: 8, paddingLeft: 0, color: 'var(--color-text-tertiary)' }}
      >
        Back to Opportunities
      </Button>
      <PageHeader
        title="Create New Opportunity"
        subtitle="Upload the RFO/RFP and let AI pre-fill the details — then review and confirm."
      />

      {/* Step 1 — AI intake from the source document */}
      <div className="ui-surface ui-animate-in" style={{ padding: 24, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
          <span className="intake-step">1</span>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text-primary)', display: 'flex', alignItems: 'center', gap: 8 }}>
              Start with your RFO document
              <ThunderboltOutlined style={{ color: 'var(--color-primary)', fontSize: 14 }} />
            </div>
            <Text type="secondary" style={{ fontSize: 13 }}>
              AI reads the solicitation and pre-fills the form below. Optional — you can type it in manually.
            </Text>
          </div>
        </div>

        <div style={{ marginTop: 16 }}>
          <Dragger
            className="intake-dragger"
            fileList={fileList}
            onChange={({ fileList: newFileList }) => setFileList(newFileList)}
            beforeUpload={() => false}
            multiple
            accept=".pdf,.doc,.docx,.xls,.xlsx,.zip,.rar"
          >
            <div style={{ padding: '14px 0' }}>
              <div className="intake-drop-medallion"><InboxOutlined /></div>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-text-primary)' }}>
                Click or drag your RFO/RFP here
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', marginTop: 4 }}>
                PDF, Word, Excel or ZIP · up to 50MB
              </div>
            </div>
          </Dragger>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
          <Button
            type="primary"
            size="large"
            icon={<ThunderboltOutlined />}
            loading={extracting}
            disabled={!hasFile}
            onClick={handleExtract}
          >
            {extracting ? 'Reading document…' : 'Extract with AI'}
          </Button>
          {aiResult && aiResult.count > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--color-success)', fontSize: 13, fontWeight: 600 }}>
              <CheckCircleFilled /> {aiResult.count} field{aiResult.count !== 1 ? 's' : ''} pre-filled below
            </span>
          )}
        </div>

        {aiResult && aiResult.count === 0 && aiResult.note && (
          <Alert style={{ marginTop: 12 }} type="info" showIcon message={aiResult.note} />
        )}
      </div>

      {/* Step 2 — Review & confirm */}
      <div className="ui-surface ui-animate-in" style={{ padding: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <span className="intake-step">2</span>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-text-primary)' }}>
            Review &amp; confirm
          </div>
        </div>
        <Form form={form} layout="vertical" onFinish={onFinish} requiredMark="optional">
          <Form.Item
            label={<>Title{aiFields.has('title') && <AiBadge />}</>}
            name="title"
            rules={[{ required: true, message: 'Please enter a title' }]}
          >
            <Input size="large" placeholder="Enter opportunity title" />
          </Form.Item>
          <Form.Item
            label={<>Agency Name{aiFields.has('clientName') && <AiBadge />}</>}
            name="clientName"
            rules={[{ required: true, message: 'Please enter the agency name' }]}
          >
            <Input size="large" placeholder="Enter agency name" />
          </Form.Item>
          <Form.Item
            label={<>Description{aiFields.has('description') && <AiBadge />}</>}
            name="description"
            rules={[{ required: true, message: 'Please enter a description' }]}
          >
            <TextArea rows={4} placeholder="Describe the opportunity" />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={<>RFO Number{aiFields.has('rfoNumber') && <AiBadge />}</>}
                name="rfoNumber"
              >
                <Input size="large" placeholder="e.g. RFO-2026-0042" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={<>Submission Method{aiFields.has('submissionMethod') && <AiBadge />}</>}
                name="submissionMethod"
              >
                <Select
                  size="large"
                  placeholder="Select a method"
                  allowClear
                  options={SUBMISSION_METHODS.map((m) => ({ value: m, label: m }))}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                label={<>Procurement Portal{aiFields.has('sourceType') && <AiBadge />}</>}
                name="sourceType"
              >
                <Input size="large" placeholder="e.g. SAM.gov, GovWin" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                label={<>Due Date{aiFields.has('deadline') && <AiBadge />}</>}
                name="deadline"
              >
                <DatePicker size="large" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label={<>RFO Link{aiFields.has('sourceUrl') && <AiBadge />}</>}
            name="sourceUrl"
          >
            <Input size="large" placeholder="Link to the original posting" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: 8, paddingTop: 18, borderTop: '1px solid var(--color-border-light)' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <Button size="large" onClick={() => navigate('/opportunities')}>Cancel</Button>
              <Button type="primary" size="large" icon={<RocketOutlined />} htmlType="submit" loading={loading}>
                Create Opportunity
              </Button>
            </div>
          </Form.Item>
        </Form>
      </div>
    </div>
  );
};

export default OpportunityCreatePage;
